/**
 * AuthProvider unificado.
 * Decide entre Local (Dexie) y MSAL (SharePoint) segun VITE_BACKEND.
 */

import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { useEffect, useMemo, type ReactNode } from "react";
import { msalConfig } from "./msalConfig";
import { setMsalInstance } from "./msalInstance";
import { LocalAuthProvider } from "./LocalAuthProvider";
import { LocalAuthBridge, MsalAuthBridge } from "./authBridges";
import { SpCookieAuthProvider } from "./SpCookieAuthProvider";

const BACKEND = import.meta.env.VITE_BACKEND ?? "sharepoint";

// ¿Hay App Registration de MSAL configurada? Si no, en modo SharePoint se usa
// el login por COOKIE del sitio (sin registrar app en Azure AD).
const MSAL_CLIENT_ID = import.meta.env.VITE_MSAL_CLIENT_ID ?? "";
const MSAL_CONFIGURED =
  MSAL_CLIENT_ID !== "" && MSAL_CLIENT_ID !== "00000000-0000-0000-0000-000000000000";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Login local (clave-maestra) para backends sin Azure AD: Dexie (demo local)
  // y Supabase (piloto compartido fuera del SP de Tecpetrol). MSAL solo aplica
  // al backend SharePoint dentro del tenant corporativo.
  if (BACKEND === "dexie" || BACKEND === "supabase") {
    return (
      <LocalAuthProvider>
        <LocalAuthBridge>{children}</LocalAuthBridge>
      </LocalAuthProvider>
    );
  }
  // SharePoint: MSAL si hay app registrada; si no, login por cookie del sitio.
  if (MSAL_CONFIGURED) {
    return <MsalAuthProviderWrapper>{children}</MsalAuthProviderWrapper>;
  }
  return <SpCookieAuthProvider>{children}</SpCookieAuthProvider>;
}

function MsalAuthProviderWrapper({ children }: AuthProviderProps) {
  const msalInstance = useMemo(() => {
    const i = new PublicClientApplication(msalConfig);
    // Exponer la instancia para que `core/mail/graphMailer.ts` la use.
    setMsalInstance(i);
    return i;
  }, []);

  useEffect(() => {
    void msalInstance.initialize().then(() => {
      msalInstance.handleRedirectPromise().catch((err) => {
        console.error("MSAL redirect error", err);
      });

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 1 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0] ?? null);
      }

      msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as { account?: import("@azure/msal-browser").AccountInfo };
          if (payload.account) {
            msalInstance.setActiveAccount(payload.account);
          }
        }
      });
    });
  }, [msalInstance]);

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthBridge>{children}</MsalAuthBridge>
    </MsalProvider>
  );
}
